import type { ReactElement } from 'react';
import { Box, Typography, Chip, Tooltip } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  RadioButtonUnchecked as PendingIcon,
  Cancel as CancelledIcon,
  SkipNext as SkippedIcon,
} from '@mui/icons-material';
import type { PipelineStage, PipelineStageName, PipelineStageStatus } from '../services/api-client';

interface PipelineStageStepper {
  stages: Record<PipelineStageName, PipelineStage>;
}

const STAGE_LABELS: Record<PipelineStageName, string> = {
  scan: 'Scan',
  process: 'Process',
  image_download: 'Image Download',
  image_embed: 'Image Embed',
};

const STATUS_CONFIG: Record<PipelineStageStatus, { color: 'default' | 'primary' | 'success' | 'error' | 'warning'; icon: ReactElement; label: string }> = {
  pending: { color: 'default', icon: <PendingIcon fontSize="small" />, label: 'Pending' },
  running: { color: 'primary', icon: <PendingIcon fontSize="small" />, label: 'Running' },
  completed: { color: 'success', icon: <CheckCircleIcon fontSize="small" />, label: 'Completed' },
  failed: { color: 'error', icon: <ErrorIcon fontSize="small" />, label: 'Failed' },
  cancelled: { color: 'warning', icon: <CancelledIcon fontSize="small" />, label: 'Cancelled' },
  skipped: { color: 'default', icon: <SkippedIcon fontSize="small" />, label: 'Skipped' },
};

function formatDuration(startedAt?: string, completedAt?: string): string {
  if (!startedAt) return '';
  const start = new Date(startedAt).getTime();
  const end = completedAt ? new Date(completedAt).getTime() : Date.now();
  const seconds = Math.floor((end - start) / 1000);
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins < 60) return `${mins}m ${secs}s`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ${mins % 60}m`;
}

function StageNode({ name, stage }: { name: PipelineStageName; stage: PipelineStage }) {
  const config = STATUS_CONFIG[stage.status] || STATUS_CONFIG.pending;
  const isRunning = stage.status === 'running';

  return (
    <Tooltip title={stage.cloud_build_status ? `Cloud Build: ${stage.cloud_build_status}` : ''}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.5,
          minWidth: 110,
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: isRunning ? 'primary.main' : `${config.color}.light`,
            color: isRunning ? 'primary.contrastText' : `${config.color}.dark`,
            border: isRunning ? '3px solid' : '2px solid',
            borderColor: isRunning ? 'primary.main' : `${config.color}.main`,
            animation: isRunning ? 'pulse 2s ease-in-out infinite' : 'none',
            '@keyframes pulse': {
              '0%, 100%': { boxShadow: '0 0 0 0 rgba(60, 89, 69, 0.4)' },
              '50%': { boxShadow: '0 0 0 8px rgba(60, 89, 69, 0)' },
            },
          }}
        >
          {config.icon}
        </Box>
        <Typography variant="caption" fontWeight={600} textAlign="center">
          {STAGE_LABELS[name]}
        </Typography>
        <Chip
          label={config.label}
          color={config.color}
          size="small"
          variant={isRunning ? 'filled' : 'outlined'}
          sx={{ height: 20, fontSize: '0.65rem' }}
        />
        {stage.started_at && (
          <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
            {formatDuration(stage.started_at, stage.completed_at)}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
}

function Connector({ active }: { active: boolean }) {
  return (
    <Box
      sx={{
        width: 40,
        height: 2,
        bgcolor: active ? 'primary.main' : 'divider',
        alignSelf: 'flex-start',
        mt: '23px', // center with 48px circle
      }}
    />
  );
}

export default function PipelineStageStepper({ stages }: PipelineStageStepper) {
  const scanDone = stages.scan?.status === 'completed';
  const imgDlDone = stages.image_download?.status === 'completed';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, py: 2, px: 1 }}>
      {/* Main flow: Scan -> fork */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0 }}>
        <StageNode name="scan" stage={stages.scan || { status: 'pending' }} />
        <Connector active={scanDone} />

        {/* Parallel branches */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Branch 1: Process */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
            <StageNode name="process" stage={stages.process || { status: 'pending' }} />
          </Box>

          {/* Branch 2: Image Download -> Image Embed */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
            <StageNode name="image_download" stage={stages.image_download || { status: 'pending' }} />
            <Connector active={imgDlDone} />
            <StageNode name="image_embed" stage={stages.image_embed || { status: 'pending' }} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
